// 261005 E2-P2a — client Trợ lý AI chung. Endpoints: /api/v1/admin/film/adaptation/assistant/*
import { apiClient } from './client';
import type { ConsequencePreview } from '../../types/adaptationWorkbench';
import type {
  ApplyBlockResult, ProposeRewriteBody, ProposeRestoreBody, ProposeAcceptPlantBody, ReapplyBlockResult, UndoBlockResult, AssistantThread, AssistantVersion, ExecuteActionResult, PreviewStarted, RelinkResult, RestoreResult, ScopeParams,
  ScopeView, SendBody, SendResult, ThreadDetail, UndoAutoRelinkResult,
} from '../../types/adaptationAssistant';

const BASE = '/api/v1/admin/film/adaptation/assistant';

export const adaptationAssistantApi = {
  /** Dữ liệu hiển thị của scope (head/editBlocker/chips/relink). 0 AI. 404 `STAGE_NOT_SUPPORTED` / `EPISODE_NOT_FOUND`. */
  scope: (p: ScopeParams) => apiClient.get<ScopeView>(`${BASE}/scope`, { params: p }),

  listThreads: (p: ScopeParams) => apiClient.get<AssistantThread[]>(`${BASE}/threads`, { params: p }),

  getThread: (threadId: string) => apiClient.get<ThreadDetail>(`${BASE}/threads/${threadId}`),

  createThread: (p: ScopeParams) => apiClient.post<AssistantThread>(`${BASE}/threads`, p),

  /** Đồng bộ, ĐÚNG 1 lần gọi AI. `requestId` sinh mỗi lần bấm Gửi — gửi lại cùng id ⇒ server trả lượt cũ, không gọi AI lần 2. */
  send: (threadId: string, body: SendBody) => apiClient.post<SendResult>(`${BASE}/threads/${threadId}/interactions`, body),

  /** ④ "Nhờ AI viết lại": lượt KHÔNG AI + khối PROPOSED `{kind:'rewrite', eps}`. 400 `INVALID_EPISODES` · 409 `EPISODES_STALE` · `THREAD_BUSY`. */
  proposeRewrite: (threadId: string, body: ProposeRewriteBody) => apiClient.post<SendResult>(`${BASE}/threads/${threadId}/propose-rewrite`, body),

  /** ⑤ "Khôi phục thông tin cảnh" (261015 P2a, $0): lượt KHÔNG AI + khối READY (xem thử đồng bộ đã chạy). 409 `NOTHING_TO_RESTORE` · `THREAD_BUSY`. */
  proposeRestore: (threadId: string, body: ProposeRestoreBody) => apiClient.post<SendResult>(`${BASE}/threads/${threadId}/propose-restore`, body),

  /** ⑤ "Chấp nhận (cảnh đã có chi tiết)…" (261015 P2b-3, $0, có lý do): lượt KHÔNG AI + khối READY `acceptPlantWritten`. 400 `REASON_INVALID` · 403 `ACTOR_NOT_HUMAN` · 409 `NOTHING_TO_ACCEPT`/`THREAD_BUSY`/`OUTLINE_NOT_APPROVED`. */
  proposeAcceptPlant: (threadId: string, body: ProposeAcceptPlantBody) => apiClient.post<SendResult>(`${BASE}/threads/${threadId}/propose-accept-plant`, body),

  /** ⑤: 202 nền (khoá WRITE_SCRIPTS); ②/④-ops: 200 `{sync:true}`; ④-rewrite: 202 nền (khoá SPLIT_TEXT). Kết quả xem ở GET thread. */
  previewBlock: (blockId: string) => apiClient.post<PreviewStarted>(`${BASE}/blocks/${blockId}/preview`, {}),

  /** KHÔNG nhận body lệnh: server áp đúng ứng viên đã lưu. */
  applyBlock: (blockId: string) => apiClient.post<ApplyBlockResult>(`${BASE}/blocks/${blockId}/apply`, {}),

  /** "Khôi phục bản trước" (không body, 0 AI). 409 `SCRIPT_CHANGED` · `VERSION_PRUNED` · `VERSION_MISMATCH` · `STATE_CONFLICT` · `JOB_RUNNING`. */
  undoBlock: (blockId: string) => apiClient.post<UndoBlockResult>(`${BASE}/blocks/${blockId}/undo`, {}),

  /** "Áp lại" (không body, 0 AI). 409 `CONTENT_DIVERGED` (cần Xem thử lại) · `SCRIPT_CHANGED` · `STATE_CONFLICT` · `JOB_RUNNING`. */
  reapplyBlock: (blockId: string) => apiClient.post<ReapplyBlockResult>(`${BASE}/blocks/${blockId}/reapply`, {}),

  dismissBlock: (blockId: string) => apiClient.post<unknown>(`${BASE}/blocks/${blockId}/dismiss`, {}),

  /** `confirm: true` bắt buộc khi hành động tốn chi phí (`ai`/`money`) — 400 `CONFIRM_REQUIRED`. FE không gửi params. */
  executeAction: (occurrenceId: string, body: { confirm?: boolean } = {}) =>
    apiClient.post<ExecuteActionResult>(`${BASE}/actions/${occurrenceId}/execute`, body),

  listVersions: (p: ScopeParams) => apiClient.get<AssistantVersion[]>(`${BASE}/versions`, { params: p }),

  /** `expectedModifiedAt` bắt buộc (400 `EXPECTED_MODIFIED_AT_REQUIRED`); lệch ⇒ 409 `SCRIPT_CHANGED`. */
  restoreVersion: (versionId: string, body: ScopeParams & { expectedModifiedAt: string }) =>
    apiClient.post<RestoreResult>(`${BASE}/versions/${versionId}/restore`, body),

  /** 3h4c — hộp hậu quả CHỈ ĐỌC của Hoàn tác / Dùng lại bản máy sửa (GET, 0 ghi / 0 AI). `base.scriptModifiedAt` ⇒ `expectedModifiedAt` của `restore`. 404 `VERSION_NOT_FOUND` · 409 `NOT_DRAFT`. */
  restoreImpact: (versionId: string, p: ScopeParams) =>
    apiClient.get<ConsequencePreview>(`${BASE}/versions/${versionId}/restore-impact`, { params: p }),

  /** NGƯỜI bấm "Nối lại". `expectedModifiedAt` = `modifiedAt` của tập N+1. 404 `RELINK_NOT_FOUND` · 409 `VERDICT_STALE`. */
  relink: (relinkId: string, body: { expectedModifiedAt: string; reason?: string }) =>
    apiClient.post<RelinkResult>(`${BASE}/relinks/${relinkId}/relink`, body),

  /**
   * 261015 P4a (D9) — NGƯỜI bấm "Hoàn tác" một lần HỆ THỐNG tự nối (AI xác nhận khớp). `expectedModifiedAt` = `modifiedAt` của tập N+1.
   * 404 `RELINK_NOT_FOUND` · `VERSION_NOT_FOUND` · 409 `NOT_AUTO_RELINK` · `RELINK_SUPERSEDED` · `SCRIPT_CHANGED` · `STATE_CONFLICT` · `JOB_RUNNING`.
   */
  undoAutoRelink: (relinkId: string, body: { expectedModifiedAt: string }) =>
    apiClient.post<UndoAutoRelinkResult>(`${BASE}/relinks/${relinkId}/undo-auto`, body),
};
